/**
 * PaginationProperties
 */

export type PaginationProperties = {
  page: number,
  rowsPerPage: number,
  totalItems: number,
}

/**
 * SetPaginationProperties
 */

export type SetPaginationProperties = {
  setPage: (p: number | ((p: number) => number)) => void,
  setRowsPerPage: (p: number | ((p: number) => number)) => void,
  setTotalItems: (p: number | ((p: number) => number)) => void,
};

/**
 * InitializePaginationProperties
 */

export const InitializePaginationProperties = (
  defaultValues?: {
    page?: number,
    rowsPerPage?: number,
    totalItems?: number,
  }
): PaginationProperties => {
  return {
    page: defaultValues?.page ?? 1,
    rowsPerPage: defaultValues?.rowsPerPage ?? 100,
    totalItems: defaultValues?.totalItems ?? 0,
  }
};

/**
 * SetPaginationPropertiesImpl
 */

const SetFunctionNoFieldImpl = <T, S extends {},>(
  set: (state: S | ((state: S) => S)) => void,
  field: keyof S,
  value: (T | ((prevValue: T) => T)),
) => typeof value === 'function'
    ? set((s) => ({ ...s, [field]: (value as (prevValue: T) => T)(s[field] as T) }))
    : set((s) => ({ ...s, [field]: value }));

/**
 * SetPaginationPropertiesImpl
 */

const SetFunctionImpl = <T, S extends {}, U extends PaginationProperties,>(
  set: (state: S | ((state: S) => S)) => void,
  field: keyof S,
  subField: keyof U,
  value: (T | ((prevValue: T) => T)),
) => typeof value === 'function'
    ? set((s) => ({ ...s, [field]: { ...s[field], [subField]: (value as (prevValue: T) => T)((s[field] as U)[subField] as T) } }))
    : set((s) => ({ ...s, [field]: { ...s[field], [subField]: value } }));

/**
 * InitializeSetPaginationProperties
 */

export const InitializeSetPaginationProperties = <S extends {},>(
  set: (state: S | ((state: S) => S)) => void,
  property: keyof S | undefined = undefined,
): SetPaginationProperties => {
  if (property) {
    return ({
      setPage: (vfn) => SetFunctionImpl(set, property, 'page', vfn),
      setRowsPerPage: (vfn) => SetFunctionImpl(set, property, 'rowsPerPage', vfn),
      setTotalItems: (vfn) => SetFunctionImpl(set, property, 'totalItems', vfn),
    });
  }
  return ({
    setPage: (vfn) => SetFunctionNoFieldImpl(set, 'page' as keyof S, vfn),
    setRowsPerPage: (vfn) => SetFunctionNoFieldImpl(set, 'rowsPerPage' as keyof S, vfn),
    setTotalItems: (vfn) => SetFunctionNoFieldImpl(set, 'totalItems' as keyof S, vfn),
  });
}